/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import SelectPicker from 'rsuite/SelectPicker';
import DateRangePicker from 'rsuite/DateRangePicker';
import {
	RefreshCcw, X, Camera, Thermometer, Weight, Wifi, ExternalLink, ScrollText,
} from 'lucide-react';
import { formActions } from '@/Actions';
import { notificationServices } from '@/Services';
import myMachinesServices from '@/Services/AssetServices/myMachine.service';
import StatsComp from '@/Pages/ProductionAnalysis/StatsComp';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { BackdropLoader } from '@/Components/Loaders';
import { useAuth } from '@/Components/Auth/Login/auth';

const REFRESH_MS = 5 * 60 * 1000;
const ONLINE_WINDOW_MIN = 70;
const LOG_PAGE_SIZE = 50;

const TABLE_HEADER_ROW = 'bg-amber-50';
const TABLE_HEADER_CELL = 'bg-amber-50 !py-2 !px-2 !text-[12px]';

const THRESHOLDS = {
	ram: 85,
	cpu: 90,
	cpuTemp: 90,
	disk: 90,
	latency: 300,
};

const pingTs = (row) => row?.machineTsSec ?? row?.machineTimeSec ?? row?.machineTs;

const toMoment = (ts) => {
	if (ts === null || ts === undefined || ts === '') return null;
	if (typeof ts === 'number' || /^\d+(\.\d+)?$/.test(String(ts))) {
		return moment.unix(Number(ts));
	}
	return moment(ts);
};

const isOnline = (ts) => {
	const m = toMoment(ts);
	return !!m && m.isValid() && moment().diff(m, 'minutes') <= ONLINE_WINDOW_MIN;
};

const fmtNum = (v, suffix = '', digits = 1) => {
	if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
	return `${Number(v).toFixed(digits).replace(/\.0$/, '')}${suffix}`;
};

const fmtUptime = (sec) => {
	if (sec === null || sec === undefined) return '-';
	const d = Math.floor(sec / 86400);
	const h = Math.floor((sec % 86400) / 3600);
	const m = Math.floor((sec % 3600) / 60);
	if (d > 0) return `${d}d ${h}h`;
	if (h > 0) return `${h}h ${m}m`;
	return `${m}m`;
};

const fmtTs = (ts) => {
	const m = toMoment(ts);
	return m && m.isValid() ? m.format('DD/MM/YY HH:mm:ss') : '-';
};

const alertClass = (value, threshold) => (
	value !== null && value !== undefined && Number(value) >= threshold
		? 'text-red-600 font-bold'
		: 'text-gray-700'
);

const reasonBadgeClass = (reason = '') => {
	const r = String(reason || '');
	if (r === 'snapshot') return 'bg-gray-100 text-gray-500';
	if (r === 'first_record') return 'bg-blue-100 text-blue-700';
	if (/connected:true->false|disconnect/i.test(r)) return 'bg-red-100 text-red-700';
	if (/connected:false->true/i.test(r)) return 'bg-green-100 text-green-700';
	if (r.includes('cpu_temp')) return 'bg-orange-100 text-orange-700';
	if (/ram_spike|disk_high|latency_spike/.test(r)) return 'bg-amber-100 text-amber-800';
	if (r.startsWith('mode:')) return 'bg-indigo-100 text-indigo-700';
	return 'bg-gray-100 text-gray-600';
};

const LOG_FILTERS = [
	{ key: 'all', label: 'All', test: () => true },
	{
		key: 'alerts',
		label: 'Alerts only',
		test: (r) => r !== 'snapshot' && r !== 'first_record',
	},
	{
		key: 'disconnects',
		label: 'Disconnects',
		test: (r) => /connected:true->false|disconnect/i.test(r),
	},
	{
		key: 'temp',
		label: 'Temp events',
		test: (r) => String(r || '').includes('cpu_temp'),
	},
];

const SensorDot = ({ connected, icon: Icon, label, extra }) => (
	<span
		title={`${label}: ${connected ? 'connected' : 'DISCONNECTED'}${extra ? ` · ${extra}` : ''}`}
		className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded ${connected
			? 'text-green-700'
			: 'bg-red-100 text-red-600'}`}
	>
		<Icon size={14} />
	</span>
);

const sensorsCell = (row) => (
	<div className='flex items-center gap-1 px-1'>
		<SensorDot
			connected={row?.weight?.connected}
			icon={Weight}
			label='Weight'
			extra={row?.weight?.valueDg != null ? `${(row.weight.valueDg / 10).toFixed(1)} g${row?.weight?.stable ? ' (stable)' : ''}` : ''}
		/>
		<SensorDot
			connected={row?.temperature?.connected}
			icon={Thermometer}
			label='Temperature'
			extra={row?.temperature?.min != null ? `${row.temperature.min}–${row.temperature.max}°C` : ''}
		/>
		<SensorDot connected={row?.camera?.connected} icon={Camera} label='Camera' />
		<SensorDot connected={row?.network?.connected} icon={Wifi} label='Network' />
	</div>
);

// ------------------------------ Logs modal ------------------------------
const MachineLogsModal = ({ machine, storeName, onClose }) => {
	const [logs, setLogs] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [filterKey, setFilterKey] = useState('all');
	// [from, to] Date pair from the rsuite DateRangePicker; fetch fires on OK.
	const [dateRange, setDateRange] = useState(null);

	const fetchLogs = useCallback(async (pageArg, rangeArg) => {
		if (!machine?.machineId) return;
		setLoading(true);
		try {
			const data = {
				machineId: machine.machineId,
				page: pageArg,
				pageSize: LOG_PAGE_SIZE,
			};
			const range = rangeArg === undefined ? dateRange : rangeArg;
			if (range?.[0]) data.fromTs = moment(range[0]).startOf('day').unix();
			if (range?.[1]) data.toTs = moment(range[1]).endOf('day').unix();
			const res = await myMachinesServices.fetchMachineHealthLogs({
				req: { data },
			});
			if (res?.statusCode === 200) {
				setLogs(res?.data || []);
				setTotal(res?.total ?? 0);
			} else {
				notificationServices.generateNotification({
					type: 'error',
					message: res?.message || 'Failed to load machine logs.',
				});
			}
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: typeof error === 'string' ? error : 'Failed to load machine logs.',
			});
		} finally {
			setLoading(false);
		}
	}, [machine?.machineId, dateRange]);

	useEffect(() => {
		setPage(1);
		fetchLogs(1);
	}, [machine?.machineId]);

	const handleRangeOk = (range) => {
		setDateRange(range);
		setPage(1);
		fetchLogs(1, range);
	};

	const handleRangeClean = () => {
		setDateRange(null);
		setPage(1);
		fetchLogs(1, null);
	};

	const changePage = (next) => {
		setPage(next);
		fetchLogs(next);
	};

	const totalPages = Math.max(1, Math.ceil(total / LOG_PAGE_SIZE));
	const activeFilter = LOG_FILTERS.find((f) => f.key === filterKey) || LOG_FILTERS[0];
	const visibleLogs = logs.filter((l) => activeFilter.test(String(l?.changeReason || '')));

	const logColumns = useMemo(() => [
		{ key: 'time', title: 'Time', width: 140 },
		{ key: 'changeReason', title: 'Change Reason', width: 320 },
		{ key: 'cpuTemp', title: 'CPU Temp', width: 90 },
		{ key: 'cpuPct', title: 'CPU %', width: 90 },
		{ key: 'ramPct', title: 'RAM %', width: 90 },
		{ key: 'diskPct', title: 'Disk %', width: 90 },
		{ key: 'latency', title: 'Latency', width: 90 },
		{ key: 'sensors', title: 'Sensors (W/T/C/N)', width: 140 },
		{ key: 'mode', title: 'Mode', width: 100 },
	], []);

	const logRowData = useMemo(() => ({
		time: (row) => (
			<span className='whitespace-nowrap px-1 text-[12px] text-gray-600'>{fmtTs(pingTs(row))}</span>
		),
		changeReason: (row) => (
			<span
				className={`inline-block max-w-[300px] truncate rounded px-1.5 py-0.5 text-[11px] font-semibold ${reasonBadgeClass(row?.changeReason)}`}
				title={row?.changeReason}
			>
				{row?.changeReason || '-'}
			</span>
		),
		cpuTemp: (row) => (
			<span className={`px-1 text-[12px] whitespace-nowrap ${alertClass(row?.cpu?.temp?.currentC, THRESHOLDS.cpuTemp)}`}>
				{fmtNum(row?.cpu?.temp?.currentC, '°C')}
			</span>
		),
		cpuPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.cpu?.usedPct, THRESHOLDS.cpu)}`}>{fmtNum(row?.cpu?.usedPct, '%')}</span>
		),
		ramPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.ram?.usedPct, THRESHOLDS.ram)}`}>{fmtNum(row?.ram?.usedPct, '%')}</span>
		),
		diskPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.disk?.usedPct, THRESHOLDS.disk)}`}>{fmtNum(row?.disk?.usedPct, '%')}</span>
		),
		latency: (row) => (
			<span className={`px-1 text-[12px] whitespace-nowrap ${alertClass(row?.network?.latencyMs, THRESHOLDS.latency)}`}>
				{fmtNum(row?.network?.latencyMs, ' ms', 0)}
			</span>
		),
		sensors: sensorsCell,
		mode: (row) => (
			<span className='px-1 text-[12px] text-gray-600'>{row?.currentMode || '-'}</span>
		),
	}), []);

	return (
		<div className='fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4'>
			<div className='flex h-[90vh] w-[1150px] max-w-[96vw] flex-col overflow-hidden rounded-xl bg-white shadow-2xl'>
				{/* header */}
				<div className='flex items-start justify-between border-b border-gray-200 px-5 py-3'>
					<div className='flex flex-col gap-0.5 text-left'>
						<span className='text-[16px] font-bold text-gray-800'>
							{machine?.machineName || `Machine #${machine?.machineId}`}
							<span className={`ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full ${isOnline(pingTs(machine))
								? 'bg-green-100 text-green-700'
								: 'bg-gray-200 text-gray-500'}`}
							>
								{isOnline(pingTs(machine)) ? '● Online' : '○ Offline'}
							</span>
						</span>
						<span className='text-[12px] text-gray-500'>
							{storeName || `Store ${machine?.storeId}`} · SN {machine?.serialNumber || '-'} · MAC {machine?.mac || '-'} · v{machine?.softwareVersion || '-'}
							{machine?.anydeskId ? ` · AnyDesk ${machine.anydeskId}` : ''}
						</span>
						<span className='text-[12px] text-gray-500'>
							Last ping: {fmtTs(pingTs(machine))} · OS boot: {fmtTs(machine?.machineBootAt)} ({fmtUptime(machine?.machineUptimeSec)}) · App start: {fmtTs(machine?.processStartAt)} ({fmtUptime(machine?.processUptimeSec)})
						</span>
					</div>
					<div className='flex items-center gap-3'>
						{machine?.camera?.snapshotUrl && (
							<a
								href={machine.camera.snapshotUrl}
								target='_blank'
								rel='noopener noreferrer'
								className='flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline'
							>
								<Camera size={14} />
								Snapshot
								<ExternalLink size={12} />
							</a>
						)}
						<button
							type='button'
							onClick={onClose}
							className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
						>
							<X size={20} />
						</button>
					</div>
				</div>

				{/* filter bar */}
				<div className='flex flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-2'>
					<div className='flex rounded-md border border-gray-200 overflow-hidden'>
						{LOG_FILTERS.map((f) => (
							<button
								key={f.key}
								type='button'
								onClick={() => setFilterKey(f.key)}
								className={`px-3 py-1 text-[12px] font-semibold transition-colors ${filterKey === f.key
									? 'bg-amber-400 text-black'
									: 'bg-white text-gray-500 hover:bg-gray-50'}`}
							>
								{f.label}
							</button>
						))}
					</div>
					<DateRangePicker
						value={dateRange}
						onOk={handleRangeOk}
						onClean={handleRangeClean}
						format='dd/MM/yyyy'
						placeholder='Select date range'
						placement='bottomStart'
						style={{ width: 240 }}
						menuStyle={{ zIndex: 9999999 }}
					/>
					<span className='ml-auto text-[12px] text-gray-500'>
						{loading ? 'Loading…' : `${total} record(s)`}
						{filterKey !== 'all' && !loading ? ` · showing ${visibleLogs.length} of ${logs.length} on this page` : ''}
					</span>
				</div>

				{/* log table */}
				<div className='min-h-0 flex-1 overflow-hidden px-3 py-2'>
					<VariantTable
						data={visibleLogs}
						columns={logColumns}
						customRowData={logRowData}
						isLoading={loading}
						virtualization={visibleLogs.length > 30}
						headerRowClassName={TABLE_HEADER_ROW}
						headerCellClassName={TABLE_HEADER_CELL}
						customInfoLine={filterKey !== 'all'
							? 'No log records matching this filter on the current page'
							: 'No log records'}
					/>
				</div>

				{/* pagination */}
				<div className='flex items-center justify-between border-t border-gray-200 px-5 py-2'>
					<span className='text-[12px] text-gray-500'>
						Page {page} of {totalPages}
					</span>
					<div className='flex gap-2'>
						<button
							type='button'
							disabled={page <= 1 || loading}
							onClick={() => changePage(page - 1)}
							className='rounded border border-gray-300 px-3 py-1 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40'
						>
							← Prev
						</button>
						<button
							type='button'
							disabled={page >= totalPages || loading}
							onClick={() => changePage(page + 1)}
							className='rounded border border-gray-300 px-3 py-1 text-[12px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40'
						>
							Next →
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

// ------------------------------ Page ------------------------------
const MachineHealthPage = () => {
	const dispatch = useDispatch();
	const { userConfig = {} } = useAuth();
	const { userRolesDict = {} } = userConfig || {};
	const isInternal = Object?.values?.(userRolesDict ?? {}).some(
		(role) => String(role ?? '').toUpperCase() === 'INTERNAL'
	);
	const stores = useSelector((state) => state?.form?.STORES?.data?.LIST);

	const [summary, setSummary] = useState(null);
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(false);
	const [storeFilter, setStoreFilter] = useState(null);
	const [search, setSearch] = useState('');
	const [selectedMachine, setSelectedMachine] = useState(null);
	const [lastRefreshed, setLastRefreshed] = useState(null);
	const timerRef = useRef(null);

	useEffect(() => {
		dispatch(formActions.gateway({
			entity: 'STORES',
			req: {
				params: {
					id: '',
					type: '',
				},
			},
			method: 'LIST',
		}));
	}, []);

	const storeNameOf = useMemo(() => {
		const map = {};
		(stores || []).forEach((s) => {
			map[s?.id] = s?.name;
		});
		return (id) => map[id] || (id != null ? `Store ${id}` : '-');
	}, [stores]);

	const fetchAll = useCallback(async (storeId, silent = false) => {
		if (!silent) setLoading(true);
		const body = storeId ? { storeId } : {};
		try {
			const [summaryRes, dashboardRes] = await Promise.all([
				myMachinesServices.fetchMachineHealthSummary({ req: { data: body } }),
				myMachinesServices.fetchMachineHealthDashboard({ req: { data: body } }),
			]);
			if (summaryRes?.statusCode === 200) setSummary(summaryRes?.data || null);
			if (dashboardRes?.statusCode === 200) setMachines(dashboardRes?.data || []);
			if (summaryRes?.statusCode !== 200 && dashboardRes?.statusCode !== 200) {
				notificationServices.generateNotification({
					type: 'error',
					message: summaryRes?.message || 'Failed to load machine health data.',
				});
			}
			setLastRefreshed(new Date());
		} catch (error) {
			notificationServices.generateNotification({
				type: 'error',
				message: typeof error === 'string' ? error : 'Failed to load machine health data.',
			});
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	// initial + on store filter change; auto-refresh every 5 minutes (silent)
	useEffect(() => {
		fetchAll(storeFilter);
		if (timerRef.current) clearInterval(timerRef.current);
		timerRef.current = setInterval(() => fetchAll(storeFilter, true), REFRESH_MS);
		return () => timerRef.current && clearInterval(timerRef.current);
	}, [storeFilter, fetchAll]);

	const visibleMachines = useMemo(() => {
		const term = search.trim().toLowerCase();
		let rows = machines;
		if (term) {
			rows = rows.filter((m) => (
				String(m?.machineName || '').toLowerCase().includes(term)
				|| String(m?.serialNumber || '').toLowerCase().includes(term)
				|| String(m?.mac || '').toLowerCase().includes(term)
				|| storeNameOf(m?.storeId).toLowerCase().includes(term)
			));
		}
		// online machines first, then by name
		return [...rows].sort((a, b) => {
			const ao = isOnline(pingTs(a)) ? 0 : 1;
			const bo = isOnline(pingTs(b)) ? 0 : 1;
			if (ao !== bo) return ao - bo;
			return String(a?.machineName || '').localeCompare(String(b?.machineName || ''));
		});
	}, [machines, search, storeNameOf]);

	const storeOptions = useMemo(() => (stores || []).map((s) => ({
		label: s?.name,
		value: s?.id,
	})), [stores]);

	const alerts = summary?.alerts || {};
	const devices = summary?.devicesConnected || {};
	const totalAlerts = Object.values(alerts).reduce((a, b) => a + (Number(b) || 0), 0);

	// Fleet summary -> StatsComp cards
	const statsCards = useMemo(() => {
		if (!summary) return [];
		const total = summary?.total ?? 0;
		const alertParts = [
			alerts?.ramHigh ? `${alerts.ramHigh} RAM` : null,
			alerts?.cpuHigh ? `${alerts.cpuHigh} CPU` : null,
			alerts?.cpuTempHigh ? `${alerts.cpuTempHigh} temp` : null,
			alerts?.diskHigh ? `${alerts.diskHigh} disk` : null,
			alerts?.latencyHigh ? `${alerts.latencyHigh} latency` : null,
		].filter(Boolean);
		return [
			{
				label: 'Total Machines',
				value: total,
				color: '#4a70fb',
				subText: storeFilter ? 'selected store' : 'all stores',
			},
			{
				label: 'Online',
				value: summary?.online ?? '-',
				color: '#059669',
				subText: `ping in last ${ONLINE_WINDOW_MIN} min`,
			},
			{
				label: 'Offline',
				value: summary?.offline ?? '-',
				color: (summary?.offline ?? 0) > 0 ? '#dc2626' : '#6b7280',
			},
			{
				label: 'Avg CPU Temp',
				value: fmtNum(summary?.avgCpuTempC, '°C'),
				color: (summary?.avgCpuTempC ?? 0) >= THRESHOLDS.cpuTemp ? '#dc2626' : '#f59e0b',
				subText: `alert ≥ ${THRESHOLDS.cpuTemp}°C`,
			},
			{
				label: 'Avg CPU',
				value: fmtNum(summary?.avgCpuPct, '%'),
				color: (summary?.avgCpuPct ?? 0) >= THRESHOLDS.cpu ? '#dc2626' : '#8b5cf6',
				subText: `alert ≥ ${THRESHOLDS.cpu}%`,
			},
			{
				label: 'Avg RAM',
				value: fmtNum(summary?.avgRamPct, '%'),
				color: (summary?.avgRamPct ?? 0) >= THRESHOLDS.ram ? '#dc2626' : '#0ea5e9',
				subText: `alert ≥ ${THRESHOLDS.ram}%`,
			},
			{
				label: 'Avg Disk',
				value: fmtNum(summary?.avgDiskPct, '%'),
				color: (summary?.avgDiskPct ?? 0) >= THRESHOLDS.disk ? '#dc2626' : '#14b8a6',
				subText: `alert ≥ ${THRESHOLDS.disk}%`,
			},
			{
				label: 'Sensors Connected',
				value: `W ${devices?.weight ?? '-'} · T ${devices?.temperature ?? '-'} · C ${devices?.camera ?? '-'}`,
				color: '#6366f1',
				subText: `Network ${devices?.network ?? '-'} / ${total}`,
			},
			{
				label: 'Active Alerts',
				value: totalAlerts,
				color: totalAlerts > 0 ? '#dc2626' : '#059669',
				isNegative: totalAlerts > 0,
				subText: totalAlerts > 0 ? alertParts.join(' · ') : 'all clear',
			},
		];
	}, [summary, storeFilter, totalAlerts]);

	const machineColumns = useMemo(() => [
		{ key: 'machine', title: 'Machine', width: 210, fixed: true },
		{ key: 'store', title: 'Store', width: 150 },
		{ key: 'status', title: 'Status', width: 110 },
		{ key: 'lastPing', title: 'Last Ping', width: 120 },
		{ key: 'mode', title: 'Mode', width: 100 },
		{ key: 'cpuTemp', title: 'CPU Temp', width: 100 },
		{ key: 'cpuPct', title: 'CPU %', width: 90 },
		{ key: 'ramPct', title: 'RAM %', width: 90 },
		{ key: 'diskPct', title: 'Disk %', width: 90 },
		{ key: 'latency', title: 'Latency', width: 100 },
		{ key: 'sensors', title: 'Sensors (W/T/C/N)', width: 140 },
		{ key: 'uptime', title: 'OS Uptime', width: 100 },
		{ key: 'anydesk', title: 'AnyDesk', width: 110 },
		{ key: 'logs', title: 'Logs', width: 90 },
	], []);

	const machineRowData = useMemo(() => ({
		machine: (row) => {
			const online = isOnline(pingTs(row));
			return (
				<div className={`flex flex-col px-1 py-1 ${online ? '' : 'opacity-60'}`}>
					<span className='text-[12px] font-semibold text-gray-800'>{row?.machineName || `#${row?.machineId}`}</span>
					<span className='text-[11px] text-gray-400'>
						SN {row?.serialNumber || '-'} · v{row?.softwareVersion || '-'}
					</span>
				</div>
			);
		},
		store: (row) => (
			<span className='px-1 text-[12px] text-gray-600'>{storeNameOf(row?.storeId)}</span>
		),
		status: (row) => {
			const online = isOnline(pingTs(row));
			return (
				<span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${online
					? 'bg-green-100 text-green-700'
					: 'bg-gray-200 text-gray-500'}`}
				>
					{online ? '● Online' : '○ Offline'}
				</span>
			);
		},
		lastPing: (row) => (
			<span className='whitespace-nowrap px-1 text-[12px] text-gray-500' title={fmtTs(pingTs(row))}>
				{toMoment(pingTs(row))?.isValid() ? toMoment(pingTs(row)).fromNow() : '-'}
			</span>
		),
		mode: (row) => (
			<span className='px-1 text-[12px] text-gray-600'>{row?.currentMode || '-'}</span>
		),
		cpuTemp: (row) => (
			<span className={`px-1 text-[12px] whitespace-nowrap ${alertClass(row?.cpu?.temp?.currentC, THRESHOLDS.cpuTemp)}`}>
				{fmtNum(row?.cpu?.temp?.currentC, '°C')}
			</span>
		),
		cpuPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.cpu?.usedPct, THRESHOLDS.cpu)}`}>{fmtNum(row?.cpu?.usedPct, '%')}</span>
		),
		ramPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.ram?.usedPct, THRESHOLDS.ram)}`}>{fmtNum(row?.ram?.usedPct, '%')}</span>
		),
		diskPct: (row) => (
			<span className={`px-1 text-[12px] ${alertClass(row?.disk?.usedPct, THRESHOLDS.disk)}`}>{fmtNum(row?.disk?.usedPct, '%')}</span>
		),
		latency: (row) => (
			<span className={`px-1 text-[12px] whitespace-nowrap ${alertClass(row?.network?.latencyMs, THRESHOLDS.latency)}`}>
				{fmtNum(row?.network?.latencyMs, ' ms', 0)}
			</span>
		),
		sensors: sensorsCell,
		uptime: (row) => (
			<span className='whitespace-nowrap px-1 text-[12px] text-gray-600'>{fmtUptime(row?.machineUptimeSec)}</span>
		),
		anydesk: (row) => (
			<span className='whitespace-nowrap px-1 text-[12px] text-gray-600'>{row?.anydeskId || '-'}</span>
		),
		logs: (row) => (
			<button
				type='button'
				onClick={() => setSelectedMachine(row)}
				className='flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100'
			>
				<ScrollText size={13} />
				Logs
			</button>
		),
	}), [storeNameOf]);

	if (!isInternal) {
		return (
			<div className='flex h-[70vh] items-center justify-center'>
				<span className='text-[15px] text-gray-500'>
					This page is for internal users only.
				</span>
			</div>
		);
	}

	return (
		<div className='flex h-full flex-col gap-3 p-4 text-left'>
			<BackdropLoader isLoading={loading} showProgress />

			{/* header row */}
			<div className='flex flex-wrap items-center gap-3'>
				<p className='m-0 text-[20px] font-semibold'>Machine Health</p>
				<div className='ml-auto flex items-center gap-3'>
					{lastRefreshed && (
						<span className='text-[12px] text-gray-400'>
							Updated {moment(lastRefreshed).format('HH:mm:ss')} · auto-refreshes every 5 min
						</span>
					)}
					<SelectPicker
						data={storeOptions}
						value={storeFilter}
						onChange={(v) => setStoreFilter(v)}
						placeholder='All Stores'
						style={{ width: 220 }}
						placement='bottomEnd'
						menuStyle={{ zIndex: 9999999 }}
					/>
					<button
						type='button'
						onClick={() => fetchAll(storeFilter)}
						title='Refresh now'
						className='flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-700 hover:bg-gray-50'
					>
						<RefreshCcw size={14} />
						Refresh
					</button>
				</div>
			</div>

			{/* fleet summary cards */}
			<StatsComp statsCards={statsCards} />

			{/* machine grid */}
			<div className='flex items-center gap-2'>
				<input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder='Search machine / serial / MAC / store…'
					className='w-[340px] rounded-md border border-gray-200 px-3 py-1.5 text-[13px] focus:border-amber-400 focus:outline-none'
				/>
				<span className='text-[12px] text-gray-400'>
					{visibleMachines.length} of {machines.length} machine(s) · use the Logs button for a machine's event log
				</span>
			</div>

			<div className='min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200'>
				<VariantTable
					data={visibleMachines}
					columns={machineColumns}
					customRowData={machineRowData}
					isLoading={loading}
					virtualization={visibleMachines.length > 30}
					headerRowClassName={TABLE_HEADER_ROW}
					headerCellClassName={TABLE_HEADER_CELL}
					customInfoLine='No machines found'
				/>
			</div>

			{selectedMachine && (
				<MachineLogsModal
					machine={selectedMachine}
					storeName={storeNameOf(selectedMachine?.storeId)}
					onClose={() => setSelectedMachine(null)}
				/>
			)}
		</div>
	);
};

export default MachineHealthPage;
